// components/RemindersPanel.tsx
// Panel de recordatorios manuales — drawer en desktop, modal fullscreen en mobile.
//
// La secretaria / profesional lo usa para:
//  - Ver turnos que necesitan recordatorio (mañana) o confirmación (hoy)
//  - Elegir una plantilla, ver el mensaje renderizado, y abrir wa.me
//  - Marcar el estado manual: enviado / confirmado / sin respuesta / canceló
//
// Pivot migración 011.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  X, MessageCircle, CheckCircle, Clock, XCircle, HelpCircle,
  Eye, Send, AlertCircle, Loader2, RefreshCw, Bell
} from 'lucide-react';
import {
  getPendingReminders,
  getTemplates,
  renderReminder,
  markReminder,
  ReminderItem,
  ReminderManualStatus,
  MessageTemplate,
} from '../services/reminders.api';

interface RemindersPanelProps {
  professionalId: number;
  open: boolean;
  onClose: () => void;
  /** Título opcional (por defecto "Recordatorios del día") */
  title?: string;
}

type TabKey = 'tomorrow' | 'today';

/** Lo que dura la salida del panel: igual a .animate-drawer-out (index.css) */
const SALIDA_MS = 220;

const statusMeta: Record<
  ReminderManualStatus,
  { label: string; icon: React.ElementType; color: string; bg: string }
> = {
  sent: { label: 'Enviado', icon: Send, color: 'text-blue-700', bg: 'bg-blue-100' },
  confirmed: { label: 'Confirmado', icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-100' },
  no_response: { label: 'Sin respuesta', icon: HelpCircle, color: 'text-gray-600', bg: 'bg-gray-100' },
  cancelled: { label: 'Canceló', icon: XCircle, color: 'text-red-700', bg: 'bg-red-100' },
};

const RemindersPanel: React.FC<RemindersPanelProps> = ({
  professionalId,
  open,
  onClose,
  title = 'Recordatorios del día',
}) => {
  const [tab, setTab] = useState<TabKey>('tomorrow');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<{ tomorrow: ReminderItem[]; today: ReminderItem[] }>({
    tomorrow: [],
    today: [],
  });
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  // Template elegida por turno (dropdown)
  const [selectedTplByAppt, setSelectedTplByAppt] = useState<Record<number, string>>({});
  // Preview mostrado en modal
  const [previewAppt, setPreviewAppt] = useState<ReminderItem | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busyAppt, setBusyAppt] = useState<number | null>(null);

  // Despliegue lateral: el panel entra desde la derecha y sale por el mismo
  // lado. Para que se vea la salida sigue montado un rato después de que el
  // padre lo cierra. `enPantalla` se ajusta durante el render y no en un
  // efecto: con un efecto quedaba un cuadro sin panel antes de la salida.
  const [enPantalla, setEnPantalla] = useState(open);
  if (open && !enPantalla) setEnPantalla(true);
  const saliendo = enPantalla && !open;
  useEffect(() => {
    if (!saliendo) return;
    // Sin animaciones (prefers-reduced-motion) no hay nada que esperar
    const sinMovimiento = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const t = window.setTimeout(() => setEnPantalla(false), sinMovimiento ? 0 : SALIDA_MS);
    return () => window.clearTimeout(t);
  }, [saliendo]);

  // ===== Cargar data =====
  const loadAll = useCallback(async () => {
    if (!professionalId) return;
    setLoading(true);
    setError(null);
    try {
      const [reminders, tpls] = await Promise.all([
        getPendingReminders(professionalId),
        getTemplates(professionalId),
      ]);
      setItems({ tomorrow: reminders.forTomorrow, today: reminders.forToday });
      setTemplates(tpls);

      // Pre-seleccionar template default (o reminder_24h para mañana, confirmation para hoy)
      const defaultTpl = tpls.find((t) => t.is_default) || tpls[0];
      const reminderTpl = tpls.find((t) => t.template_key === 'reminder_24h') || defaultTpl;
      const confirmTpl = tpls.find((t) => t.template_key === 'confirmation') || defaultTpl;
      const map: Record<number, string> = {};
      reminders.forTomorrow.forEach((a) => {
        if (reminderTpl) map[a.id] = reminderTpl.template_key;
      });
      reminders.forToday.forEach((a) => {
        if (confirmTpl) map[a.id] = confirmTpl.template_key;
      });
      setSelectedTplByAppt(map);
    } catch (e: any) {
      setError(e.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [professionalId]);

  useEffect(() => {
    if (open) loadAll();
  }, [open, loadAll]);

  // Bloquear scroll del body cuando está abierto
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // ===== Acciones =====

  const openPreview = async (appt: ReminderItem) => {
    setPreviewAppt(appt);
    setPreviewText('');
    setPreviewLoading(true);
    try {
      const key = selectedTplByAppt[appt.id];
      const result = await renderReminder(appt.id, key);
      setPreviewText(result.rendered);
    } catch (e: any) {
      setPreviewText(`❌ Error: ${e.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  /** Abre WhatsApp Web/app con mensaje pre-redactado, y marca como 'sent' */
  const sendViaWhatsApp = async (appt: ReminderItem) => {
    if (!appt.client_phone) {
      alert('Este turno no tiene teléfono cargado.');
      return;
    }
    setBusyAppt(appt.id);
    try {
      const key = selectedTplByAppt[appt.id];
      const result = await renderReminder(appt.id, key);
      if (!result.waUrl) {
        alert('No se pudo construir el link de WhatsApp.');
        return;
      }
      // 1. Abrir WhatsApp en otra pestaña
      window.open(result.waUrl, '_blank', 'noopener,noreferrer');
      // 2. Marcar como enviado
      await markReminder(appt.id, {
        status: 'sent',
        templateKey: key,
        message_preview: result.rendered.slice(0, 200),
      });
      // 3. Actualizar state localmente sin refetch
      setItems((prev) => ({
        tomorrow: prev.tomorrow.map((a) =>
          a.id === appt.id ? { ...a, manual_last_status: 'sent' } : a
        ),
        today: prev.today.map((a) =>
          a.id === appt.id ? { ...a, manual_last_status: 'sent' } : a
        ),
      }));
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setBusyAppt(null);
    }
  };

  /** Marca un estado manual sin abrir WhatsApp (confirmado / canceló / sin respuesta) */
  const markStatus = async (appt: ReminderItem, status: ReminderManualStatus) => {
    setBusyAppt(appt.id);
    try {
      await markReminder(appt.id, {
        status,
        templateKey: selectedTplByAppt[appt.id],
      });
      setItems((prev) => ({
        tomorrow: prev.tomorrow.map((a) =>
          a.id === appt.id ? { ...a, manual_last_status: status } : a
        ),
        today: prev.today.map((a) =>
          a.id === appt.id ? { ...a, manual_last_status: status } : a
        ),
      }));
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setBusyAppt(null);
    }
  };

  const currentList = tab === 'tomorrow' ? items.tomorrow : items.today;
  const totalPending = items.tomorrow.length + items.today.length;

  const tomorrowPending = useMemo(
    () => items.tomorrow.filter((a) => !a.manual_last_status).length,
    [items.tomorrow]
  );
  const todayPending = useMemo(
    () => items.today.filter((a) => !a.manual_last_status).length,
    [items.today]
  );

  if (!enPantalla) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-[199] ${saliendo ? 'animate-backdrop-out' : 'animate-backdrop-in'}`}
        onClick={onClose}
      />

      {/* Container — mobile: fullscreen modal; desktop: right drawer.
          Mientras sale no recibe toques: caen en el overlay, que no hace
          nada más que cerrar (y ya está cerrado). Un doble toque en la X
          no le llega a lo que haya debajo. */}
      <div
        className={`fixed z-[200] inset-0 md:inset-auto md:top-0 md:right-0 md:bottom-0 md:w-[520px] bg-white shadow-2xl flex flex-col ${
          saliendo ? 'animate-drawer-out pointer-events-none' : 'animate-drawer-in'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-700" />
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {totalPending > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-emerald-600 text-white rounded-full">
                {totalPending}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={loadAll}
              disabled={loading}
              className="p-2 rounded-lg hover:bg-white/60 disabled:opacity-50"
              title="Actualizar"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/60"
              title="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white">
          <button
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              tab === 'tomorrow'
                ? 'text-emerald-700 border-b-2 border-emerald-600 bg-emerald-50/50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setTab('tomorrow')}
          >
            Mañana ({tomorrowPending})
          </button>
          <button
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              tab === 'today'
                ? 'text-emerald-700 border-b-2 border-emerald-600 bg-emerald-50/50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setTab('today')}
          >
            Hoy ({todayPending})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin mb-2" />
              <span className="text-sm">Cargando turnos…</span>
            </div>
          )}

          {!loading && error && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          {!loading && !error && templates.length === 0 && (
            <div className="m-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  Todavía no hay plantillas configuradas. Andá a{' '}
                  <strong>Configuración → Notificaciones</strong> para crearlas.
                </div>
              </div>
            </div>
          )}

          {!loading && !error && currentList.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
              <span className="text-sm">
                No hay turnos {tab === 'tomorrow' ? 'para recordar mañana' : 'pendientes de confirmar hoy'}.
              </span>
            </div>
          )}

          {!loading && !error && currentList.map((appt) => {
            const currentStatus = appt.manual_last_status;
            const meta = currentStatus ? statusMeta[currentStatus] : null;
            const isBusy = busyAppt === appt.id;
            return (
              <div
                key={appt.id}
                className="border-b border-gray-100 px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                {/* Header del turno */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="font-medium">{appt.time}</span>
                      {appt.duration && <span>· {appt.duration} min</span>}
                    </div>
                    <div className="font-semibold text-gray-900 mt-0.5">
                      {appt.client_name || 'Sin nombre'}
                    </div>
                    {appt.service_name && (
                      <div className="text-xs text-gray-500 mt-0.5">{appt.service_name}</div>
                    )}
                    {appt.client_phone && (
                      <div className="text-xs text-gray-400 mt-0.5">📞 {appt.client_phone}</div>
                    )}
                  </div>
                  {meta && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.color}`}
                    >
                      <meta.icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                  )}
                </div>

                {/* Plantilla selector */}
                {templates.length > 0 && (
                  <select
                    value={selectedTplByAppt[appt.id] || ''}
                    onChange={(e) =>
                      setSelectedTplByAppt((prev) => ({
                        ...prev,
                        [appt.id]: e.target.value,
                      }))
                    }
                    className="w-full text-xs mt-1 mb-2 px-2 py-1.5 border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                    disabled={isBusy}
                  >
                    {templates.map((t) => (
                      <option key={t.id} value={t.template_key}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                )}

                {/* Acciones — resaltamos el estado elegido y atenuamos los demás */}
                {(() => {
                  const hasStatus = !!currentStatus;
                  const dim = 'opacity-40 hover:opacity-70';
                  const statusBtn = (
                    target: 'confirmed' | 'cancelled' | 'no_response',
                    base: string,
                    active: string
                  ) => {
                    if (!hasStatus) return base;
                    if (currentStatus === target) return `${active} ring-2 ring-offset-1 shadow-sm`;
                    return `${base} ${dim}`;
                  };
                  return (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <button
                        onClick={() => openPreview(appt)}
                        disabled={isBusy || templates.length === 0}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Ver mensaje
                      </button>
                      <button
                        onClick={() => sendViaWhatsApp(appt)}
                        disabled={isBusy || templates.length === 0 || !appt.client_phone}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                          hasStatus
                            ? 'bg-green-600/60 hover:bg-green-600/80'
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                        title={!appt.client_phone ? 'Sin teléfono' : 'Abre WhatsApp Web y marca como enviado'}
                      >
                        {isBusy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <MessageCircle className="w-3.5 h-3.5" />
                        )}
                        WhatsApp
                      </button>
                      <button
                        onClick={() => markStatus(appt, 'confirmed')}
                        disabled={isBusy}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md disabled:opacity-50 transition-all ${statusBtn(
                          'confirmed',
                          'bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
                          'bg-emerald-600 text-white hover:bg-emerald-700 ring-emerald-300'
                        )}`}
                        title="Marcar como confirmado"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Confirmó
                      </button>
                      <button
                        onClick={() => markStatus(appt, 'cancelled')}
                        disabled={isBusy}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md disabled:opacity-50 transition-all ${statusBtn(
                          'cancelled',
                          'bg-red-100 text-red-800 hover:bg-red-200',
                          'bg-red-600 text-white hover:bg-red-700 ring-red-300'
                        )}`}
                        title="Marcar como cancelado"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Canceló
                      </button>
                      <button
                        onClick={() => markStatus(appt, 'no_response')}
                        disabled={isBusy}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md disabled:opacity-50 transition-all ${statusBtn(
                          'no_response',
                          'bg-gray-100 text-gray-700 hover:bg-gray-200',
                          'bg-gray-600 text-white hover:bg-gray-700 ring-gray-300'
                        )}`}
                        title="Sin respuesta"
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                        Sin resp.
                      </button>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview modal */}
      {previewAppt && (
        <div
          className="fixed inset-0 bg-black/60 z-[210] flex items-center justify-center p-4"
          onClick={() => setPreviewAppt(null)}
        >
          <div
            className="bg-white rounded-xl max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-900">
                Mensaje para {previewAppt.client_name}
              </h3>
              <button
                onClick={() => setPreviewAppt(null)}
                className="p-1.5 rounded hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {previewLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans bg-gray-50 p-3 rounded-lg border border-gray-200 max-h-64 overflow-y-auto">
                  {previewText}
                </pre>
              )}
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t bg-gray-50">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(previewText);
                }}
                disabled={previewLoading || !previewText}
                className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded-md disabled:opacity-50"
              >
                Copiar
              </button>
              <button
                onClick={() => {
                  if (previewAppt) sendViaWhatsApp(previewAppt);
                  setPreviewAppt(null);
                }}
                disabled={previewLoading || !previewAppt?.client_phone}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700 rounded-md disabled:opacity-50"
              >
                <MessageCircle className="w-4 h-4" />
                Abrir WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RemindersPanel;
